import React, { useState, useEffect } from 'react';
import { API_BASE_URL } from '../../config';
import { 
  Mail, 
  MessageSquare, 
  Search, 
  Filter, 
  RefreshCw, 
  Eye, 
  XCircle, 
  CheckCircle,
  Clock,
  Send,
  Calendar,
  User,
  ShoppingCart,
  DollarSign
} from 'lucide-react';

const NotificationManagement = () => {
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(false);
  const [stats, setStats] = useState(null);
  const [filters, setFilters] = useState({
    type: 'all', // 'sms', 'email', 'all'
    status: '',
    orderId: '',
    customerId: '',
    startDate: '',
    endDate: '',
    search: '',
    page: 1,
    limit: 20
  });
  const [pagination, setPagination] = useState({ page: 1, limit: 20, total: 0, pages: 0 });
  const [selectedNotification, setSelectedNotification] = useState(null);
  const [showDetails, setShowDetails] = useState(false);
  const [resending, setResending] = useState(false);
  const [selectedFilter, setSelectedFilter] = useState(null); // 'sms', 'email', 'failed', 'all'

  useEffect(() => {
    fetchNotifications();
    fetchStats();
  }, [filters]);

  // Handle card click to filter notifications
  const handleCardClick = (filterType) => {
    setSelectedFilter(filterType);
    if (filterType === 'all') {
      setFilters({ ...filters, type: 'all', status: '', page: 1 });
    } else if (filterType === 'sms') {
      setFilters({ ...filters, type: 'sms', status: '', page: 1 });
    } else if (filterType === 'email') {
      setFilters({ ...filters, type: 'email', status: '', page: 1 });
    } else if (filterType === 'failed') {
      setFilters({ ...filters, type: 'all', status: 'FAILED', page: 1 });
    }
  };

  const fetchNotifications = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('adminToken');
      const queryParams = new URLSearchParams();
      Object.keys(filters).forEach(key => {
        if (filters[key]) queryParams.append(key, filters[key]);
      });

      const response = await fetch(
        `${API_BASE_URL}/api/admin/notifications?${queryParams}`,
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );

      if (response.ok) {
        const data = await response.json();
        setNotifications(data.notifications || []);
        setPagination(data.pagination || {});
      }
    } catch (error) {
      console.error('Error fetching notifications:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = async () => {
    try {
      const token = localStorage.getItem('adminToken');
      const queryParams = new URLSearchParams();
      if (filters.startDate) queryParams.append('startDate', filters.startDate);
      if (filters.endDate) queryParams.append('endDate', filters.endDate);

      const response = await fetch(
        `${API_BASE_URL}/api/admin/notifications/stats?${queryParams}`,
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );

      if (response.ok) {
        const data = await response.json();
        setStats(data);
      }
    } catch (error) {
      console.error('Error fetching stats:', error);
    }
  };

  const handleResend = async (notification) => {
    if (!window.confirm(`Resend ${notification.type.toUpperCase()} notification?`)) return;

    setResending(true);
    try {
      const token = localStorage.getItem('adminToken');
      const response = await fetch(
        `${API_BASE_URL}/api/admin/notifications/${notification.id}/resend?type=${notification.type}`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );

      if (response.ok) {
        alert('Notification resent successfully!');
        fetchNotifications();
      } else {
        const error = await response.json();
        alert(`Failed to resend: ${error.error || 'Unknown error'}`);
      }
    } catch (error) {
      console.error('Error resending notification:', error);
      alert('Failed to resend notification');
    } finally {
      setResending(false);
    }
  };

  const getStatusIcon = (status) => {
    switch (status?.toUpperCase()) {
      case 'SENT':
      case 'DELIVERED':
        return <CheckCircle className="h-4 w-4 text-green-500" />;
      case 'FAILED':
      case 'BOUNCED':
        return <XCircle className="h-4 w-4 text-red-500" />;
      case 'PENDING':
        return <Clock className="h-4 w-4 text-yellow-500" />;
      case 'OPENED':
      case 'CLICKED':
        return <Eye className="h-4 w-4 text-blue-500" />;
      default:
        return <Clock className="h-4 w-4 text-gray-400" />;
    }
  };

  const formatDate = (date) => {
    if (!date) return 'N/A';
    return new Date(date).toLocaleString();
  };

  return (
    <div className="p-6">
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-2xl font-bold text-gray-800">Notification Management</h2>
        <button
          onClick={() => { fetchNotifications(); fetchStats(); }}
          className="flex items-center px-4 py-2 bg-[#d44100] text-white rounded-lg hover:bg-[#b83700]"
        >
          <RefreshCw className="h-4 w-4 mr-2" />
          Refresh
        </button>
      </div>

      {/* Statistics Cards - Interactive */}
      {stats && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-6">
          <div 
            onClick={() => handleCardClick('sms')}
            className={`bg-white p-6 rounded-lg shadow-md cursor-pointer transition-all duration-200 hover:shadow-lg hover:scale-105 ${
              selectedFilter === 'sms' ? 'ring-2 ring-blue-500' : ''
            }`}
          >
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Total SMS</p>
                <p className="text-2xl font-bold text-blue-600">{stats.sms?.total || 0}</p>
                <p className="text-xs text-gray-500">
                  {stats.sms?.successRate?.toFixed(1) || 0}% success rate
                </p>
                <p className="text-xs text-blue-600 mt-1 font-medium">Click to view ({stats.sms?.total || 0} messages)</p>
              </div>
              <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center">
                <MessageSquare className="h-6 w-6 text-blue-600" />
              </div>
            </div>
          </div>

          <div 
            onClick={() => handleCardClick('email')}
            className={`bg-white p-6 rounded-lg shadow-md cursor-pointer transition-all duration-200 hover:shadow-lg hover:scale-105 ${
              selectedFilter === 'email' ? 'ring-2 ring-green-500' : ''
            }`}
          >
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Total Email</p>
                <p className="text-2xl font-bold text-green-600">{stats.email?.total || 0}</p>
                <p className="text-xs text-gray-500">
                  {stats.email?.successRate?.toFixed(1) || 0}% success rate
                </p>
                <p className="text-xs text-green-600 mt-1 font-medium">Click to view ({stats.email?.total || 0} emails)</p>
              </div>
              <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center">
                <Mail className="h-6 w-6 text-green-600" />
              </div>
            </div>
          </div>

          <div 
            onClick={() => handleCardClick('failed')}
            className={`bg-white p-6 rounded-lg shadow-md cursor-pointer transition-all duration-200 hover:shadow-lg hover:scale-105 ${
              selectedFilter === 'failed' ? 'ring-2 ring-red-500' : ''
            }`}
          >
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Failed</p>
                <p className="text-2xl font-bold text-red-600">
                  {(stats.sms?.failed || 0) + (stats.email?.failed || 0)}
                </p>
                <p className="text-xs text-gray-500">Requires attention</p>
                <p className="text-xs text-red-600 mt-1 font-medium">Click to view ({(stats.sms?.failed || 0) + (stats.email?.failed || 0)} failed)</p>
              </div>
              <div className="w-12 h-12 bg-red-100 rounded-full flex items-center justify-center">
                <XCircle className="h-6 w-6 text-red-600" />
              </div>
            </div>
          </div>

          <div 
            onClick={() => handleCardClick('all')}
            className={`bg-white p-6 rounded-lg shadow-md cursor-pointer transition-all duration-200 hover:shadow-lg hover:scale-105 ${
              selectedFilter === null || selectedFilter === 'all' ? 'ring-2 ring-purple-500' : ''
            }`}
          >
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Total Cost</p>
                <p className="text-2xl font-bold text-orange-600">KES {stats.sms?.totalCost?.toFixed(2) || '0.00'}</p>
                <p className="text-xs text-gray-500">SMS costs only</p>
                <p className="text-xs text-purple-600 mt-1 font-medium">Click to view all</p>
              </div>
              <div className="w-12 h-12 bg-orange-100 rounded-full flex items-center justify-center">
                <DollarSign className="h-6 w-6 text-orange-600" />
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Filters */}
      <div className="bg-white p-4 rounded-lg shadow mb-6">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Type</label>
            <select
              value={filters.type}
              onChange={(e) => setFilters({ ...filters, type: e.target.value, page: 1 })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg"
            >
              <option value="all">All</option>
              <option value="sms">SMS</option>
              <option value="email">Email</option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
            <select
              value={filters.status}
              onChange={(e) => setFilters({ ...filters, status: e.target.value, page: 1 })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg"
            >
              <option value="">All Statuses</option>
              <option value="SENT">Sent</option>
              <option value="DELIVERED">Delivered</option>
              <option value="FAILED">Failed</option>
              <option value="PENDING">Pending</option>
              <option value="OPENED">Opened (Email)</option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Search</label>
            <div className="relative">
              <Search className="absolute left-3 top-2.5 h-4 w-4 text-gray-400" />
              <input
                type="text"
                placeholder="Search notifications..."
                value={filters.search}
                onChange={(e) => setFilters({ ...filters, search: e.target.value, page: 1 })}
                className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-lg"
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Order ID</label>
            <input
              type="text"
              placeholder="Filter by order..."
              value={filters.orderId}
              onChange={(e) => setFilters({ ...filters, orderId: e.target.value, page: 1 })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg"
            />
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Start Date</label>
            <input
              type="date"
              value={filters.startDate}
              onChange={(e) => setFilters({ ...filters, startDate: e.target.value, page: 1 })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">End Date</label>
            <input
              type="date"
              value={filters.endDate}
              onChange={(e) => setFilters({ ...filters, endDate: e.target.value, page: 1 })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg"
            />
          </div>
        </div>
      </div>

      {/* Notifications Table */}
      <div className="bg-white rounded-lg shadow overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Type</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Recipient</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Subject/Message</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Order</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Sent At</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Actions</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {loading ? (
                <tr>
                  <td colSpan="7" className="px-6 py-4 text-center text-gray-500">
                    Loading notifications...
                  </td>
                </tr>
              ) : notifications.length === 0 ? (
                <tr>
                  <td colSpan="7" className="px-6 py-4 text-center text-gray-500">
                    No notifications found
                  </td>
                </tr>
              ) : (
                notifications.map((notification) => (
                  <tr key={notification.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        {notification.type === 'sms' ? (
                          <MessageSquare className="h-5 w-5 text-blue-500 mr-2" />
                        ) : (
                          <Mail className="h-5 w-5 text-green-500 mr-2" />
                        )}
                        <span className="text-sm font-medium text-gray-900 uppercase">
                          {notification.type}
                        </span>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900">{notification.recipient}</div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="text-sm text-gray-900">
                        {notification.subject || notification.message?.substring(0, 50) + '...'}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        {getStatusIcon(notification.status)}
                        <span className="ml-2 text-sm text-gray-900">{notification.status}</span>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      {notification.order ? (
                        <div className="flex items-center">
                          <ShoppingCart className="h-4 w-4 text-gray-400 mr-1" />
                          <span className="text-sm text-gray-900">
                            {notification.order.orderNumber}
                          </span>
                        </div>
                      ) : (
                        <span className="text-sm text-gray-400">N/A</span>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {formatDate(notification.sentAt || notification.createdAt)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                      <div className="flex space-x-2">
                        <button
                          onClick={() => {
                            setSelectedNotification(notification);
                            setShowDetails(true);
                          }}
                          className="text-[#d44100] hover:text-[#b83700]"
                        >
                          <Eye className="h-4 w-4" />
                        </button>
                        {notification.status === 'FAILED' && (
                          <button
                            onClick={() => handleResend(notification)}
                            disabled={resending}
                            className="text-green-600 hover:text-green-700"
                            title="Resend"
                          >
                            <Send className="h-4 w-4" />
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {pagination.pages > 1 && (
          <div className="bg-gray-50 px-6 py-3 flex items-center justify-between">
            <div className="text-sm text-gray-700">
              Showing {((pagination.page - 1) * pagination.limit) + 1} to{' '}
              {Math.min(pagination.page * pagination.limit, pagination.total)} of{' '}
              {pagination.total} notifications
            </div>
            <div className="flex space-x-2">
              <button
                onClick={() => setFilters({ ...filters, page: filters.page - 1 })}
                disabled={filters.page === 1}
                className="px-3 py-1 border border-gray-300 rounded-lg disabled:opacity-50"
              >
                Previous
              </button>
              <button
                onClick={() => setFilters({ ...filters, page: filters.page + 1 })}
                disabled={filters.page >= pagination.pages}
                className="px-3 py-1 border border-gray-300 rounded-lg disabled:opacity-50"
              >
                Next
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Notification Details Modal */}
      {showDetails && selectedNotification && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-2xl w-full mx-4 max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-xl font-bold">Notification Details</h3>
              <button
                onClick={() => {
                  setShowDetails(false);
                  setSelectedNotification(null);
                }}
                className="text-gray-400 hover:text-gray-600"
              >
                <XCircle className="h-6 w-6" />
              </button>
            </div>

            <div className="space-y-4">
              <div>
                <label className="text-sm font-medium text-gray-700">Type</label>
                <p className="mt-1 text-sm text-gray-900 uppercase">{selectedNotification.type}</p>
              </div>

              <div>
                <label className="text-sm font-medium text-gray-700">Recipient</label>
                <p className="mt-1 text-sm text-gray-900">{selectedNotification.recipient}</p>
              </div>

              {selectedNotification.subject && (
                <div>
                  <label className="text-sm font-medium text-gray-700">Subject</label>
                  <p className="mt-1 text-sm text-gray-900">{selectedNotification.subject}</p>
                </div>
              )}

              <div>
                <label className="text-sm font-medium text-gray-700">Message</label>
                <p className="mt-1 text-sm text-gray-900 whitespace-pre-wrap">
                  {selectedNotification.message}
                </p>
              </div>

              <div>
                <label className="text-sm font-medium text-gray-700">Status</label>
                <div className="mt-1 flex items-center">
                  {getStatusIcon(selectedNotification.status)}
                  <span className="ml-2 text-sm text-gray-900">{selectedNotification.status}</span>
                </div>
              </div>

              {selectedNotification.errorMessage && (
                <div>
                  <label className="text-sm font-medium text-red-700">Error</label>
                  <p className="mt-1 text-sm text-red-600">{selectedNotification.errorMessage}</p>
                </div>
              )}

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-sm font-medium text-gray-700">Sent At</label>
                  <p className="mt-1 text-sm text-gray-900">
                    {formatDate(selectedNotification.sentAt)}
                  </p>
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-700">Delivered At</label>
                  <p className="mt-1 text-sm text-gray-900">
                    {formatDate(selectedNotification.deliveredAt)}
                  </p>
                </div>
              </div>

              {selectedNotification.order && (
                <div>
                  <label className="text-sm font-medium text-gray-700">Order</label>
                  <p className="mt-1 text-sm text-gray-900">
                    {selectedNotification.order.orderNumber} - {selectedNotification.order.customer?.fullName}
                  </p>
                </div>
              )}

              {selectedNotification.cost && (
                <div>
                  <label className="text-sm font-medium text-gray-700">Cost</label>
                  <p className="mt-1 text-sm text-gray-900">KES {selectedNotification.cost.toFixed(2)}</p>
                </div>
              )}
            </div>

            <div className="mt-6 flex justify-end">
              {selectedNotification.status === 'FAILED' && (
                <button
                  onClick={() => {
                    handleResend(selectedNotification);
                    setShowDetails(false);
                  }}
                  disabled={resending}
                  className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50"
                >
                  {resending ? 'Resending...' : 'Resend Notification'}
                </button>
              )}
              <button
                onClick={() => {
                  setShowDetails(false);
                  setSelectedNotification(null);
                }}
                className="ml-2 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default NotificationManagement;

